import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageShell } from "@/components/Shell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { LockNotice } from "@/components/LockNotice";
import { OperatorAppointments } from "@/components/operator/OperatorAppointments";

const act = (fn, ok, reload) => async () => {
  try {
    await fn();
    toast.success(ok);
    reload();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const Agenda = () => <OperatorAppointments />;

export function Requests({ isDoctor }) {
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState({});
  const load = () => api.get("/staff/requests").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  const review = (r, status) => act(() => api.post(`/staff/requests/${r.id}/review`, { status, note: notes[r.id] || "" }), "Richiesta aggiornata", load);
  return (
    <div className="space-y-2" data-testid="staff-requests">
      {!items.length && <p className="text-sm text-slate-500">Nessuna richiesta.</p>}
      {items.map((r) => (
        <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`staff-request-${r.id}`}>
          <div className="flex items-center justify-between gap-2"><b className="text-sm">{r.type_label} · {r.patient_name}</b><StatusBadge status={r.status} /></div>
          <p className="mt-1 text-xs text-slate-600">{r.description} · {fmtDateTime(r.created_at)} · allegati: {r.document_ids.length}</p>
          {["inviata", "in_verifica"].includes(r.status) && (
            <div className="mt-2 flex flex-wrap gap-2">
              <Input className="h-8 max-w-xs text-xs" data-testid={`staff-request-note-${r.id}`} placeholder="Nota per il paziente" value={notes[r.id] || ""} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })} />
              {r.status === "inviata" && <Button size="sm" variant="outline" data-testid={`staff-request-verify-${r.id}`} onClick={review(r, "in_verifica")}>Prendi in carico</Button>}
              {isDoctor && <>
                <Button size="sm" data-testid={`staff-request-approve-${r.id}`} onClick={review(r, "approvata")} className="bg-emerald-600 hover:bg-emerald-700">Approva e firma</Button>
                <Button size="sm" variant="outline" data-testid={`staff-request-reject-${r.id}`} onClick={review(r, "rifiutata")}>Rifiuta</Button>
              </>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function SharedDocs() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/staff/documents").then(({ data }) => setItems(data)).catch(() => {});
  }, []);
  const open = async (d) => {
    const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
    window.open(URL.createObjectURL(res.data));
  };
  return (
    <div className="space-y-2" data-testid="staff-shared-docs">
      {!items.length && <p className="text-sm text-slate-500">Nessun documento condiviso con consenso.</p>}
      {items.map((d) => <div key={d.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-sm"><span>{d.filename} · {d.owner_name}</span><Button size="sm" variant="outline" onClick={() => open(d)}>Apri</Button></div>)}
    </div>
  );
}

function Handoffs() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/chat/handoffs").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  return (
    <div className="space-y-2" data-testid="staff-handoffs">
      {!items.length && <p className="text-sm text-slate-500">Nessuna richiesta di operatore.</p>}
      {items.map((h) => (
        <div key={h.id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 text-sm">
          <span>{h.user_name} ({h.user_email}) · {h.message} · {fmtDateTime(h.created_at)}</span>
          {h.status === "aperta" ? <Button size="sm" variant="outline" onClick={act(() => api.post(`/chat/handoffs/${h.id}/close`), "Chiusa", load)}>Chiudi</Button> : <span className="text-xs text-slate-400">chiusa</span>}
        </div>
      ))}
    </div>
  );
}

export default function StaffPage() {
  const { user, access } = useAuth();
  const isDoctor = user.role === "medico";
  const isClinician = ["medico", "infermiere"].includes(user.role);
  return (
    <PageShell title="Area staff" subtitle="Strumenti operativi riservati al personale autorizzato." testid="staff-page">
      {isDoctor && <div className="mb-4 max-w-2xl"><LockNotice policy={access?.policies?.EROGA_VIDEO_CONSULTO} /></div>}
      <Tabs defaultValue="agenda">
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start">
          <TabsTrigger value="agenda" data-testid="staff-tab-agenda">Agenda</TabsTrigger>
          <TabsTrigger value="richieste" data-testid="staff-tab-richieste">Richieste mediche</TabsTrigger>
          {isClinician && <TabsTrigger value="documenti" data-testid="staff-tab-documenti">Documenti condivisi</TabsTrigger>}
          <TabsTrigger value="operatore" data-testid="staff-tab-operatore">Richieste operatore</TabsTrigger>
        </TabsList>
        <TabsContent value="agenda"><Agenda /></TabsContent>
        <TabsContent value="richieste"><Requests isDoctor={isDoctor} /></TabsContent>
        {isClinician && <TabsContent value="documenti"><SharedDocs /></TabsContent>}
        <TabsContent value="operatore"><Handoffs /></TabsContent>
      </Tabs>
    </PageShell>
  );
}
